import {
  Typography,
  Button,
  Box,
  Link,
  IconButton,
  Stack,
  useTheme,
} from '@mui/material';
import Rating from '@mui/material/Rating';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from 'tss-react/mui';
import { VectorLogo } from 'components/VectorLogo';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { getDeviceInfo } from 'state/localStorageTyped';
import { useDownloadAppBannerState } from 'state/useDownloadAppBannerState';
import { ANALYTICS_EVENTS, logEvent } from 'shared/analytics';
import {
  AppStoreLinks,
  DOWNLOAD_BANNER_HEIGHT_PX,
  DOWNLOAD_APP_BANNER_OFFSET_CSS_VAR,
} from '../../constants';

const useStyles = makeStyles()(() => ({
  logo: { display: 'flex', lineHeight: 0 },
}));

export const DownloadAppBanner = (): JSX.Element | null => {
  const { t } = useTranslation();
  const deviceInfo = getDeviceInfo();
  const { showBanner, setBannerShown } = useDownloadAppBannerState();
  const theme = useTheme();
  const { classes } = useStyles();
  const logoColor = theme.palette.deepRed[900];

  // Expose banner height as a CSS variable so fixed-position drawers
  // (e.g. MapEntityDrawer, LayersDrawer) can offset their top padding.
  // NOTE: No cleanup on unmount - removing the property causes layout bugs
  // when navigating away and back (e.g. report details → incident).
  // The effect already resets to '0px' when showBanner is false.
  useEffect(() => {
    document.documentElement.style.setProperty(
      DOWNLOAD_APP_BANNER_OFFSET_CSS_VAR,
      showBanner ? `${DOWNLOAD_BANNER_HEIGHT_PX}px` : '0px',
    );
  }, [showBanner]);

  if (!showBanner) return null;

  const storeLink =
    deviceInfo?.operatingSystem === 'android'
      ? AppStoreLinks.android
      : AppStoreLinks.iosBannerCampaign;

  const logClick = (): void => {
    logEvent({
      name: ANALYTICS_EVENTS.CLICKED.DOWNLOAD_APP_BANNER,
      params: {},
    });
  };

  const closeClick = (): void => {
    logEvent({
      name: ANALYTICS_EVENTS.CLICKED.DOWNLOAD_APP_BANNER_CLOSE,
      params: {},
    });
    setBannerShown();
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        width: '100%',
        p: theme.spacing(1, 2),
        backgroundColor: 'background.paper',
        zIndex: theme.zIndex.drawer + 10,
      }}
      data-testid="download-app-banner"
    >
      <Stack direction="row" alignItems="center">
        <IconButton
          color="inherit"
          sx={{ ml: -1.5, mr: 0.5 }}
          onClick={closeClick}
          aria-label="Close banner"
          size="large"
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: 2,
              boxShadow: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.palette.brand[500],
            }}
          >
            <VectorLogo size={36} color={logoColor} className={classes.logo} />
          </Box>

          <Box>
            <Typography sx={{ fontSize: 12, lineHeight: 1.25, m: 0 }}>
              <b>{t('downloadAppBanner.title')}</b>
            </Typography>

            <Typography sx={{ fontSize: 11, lineHeight: 1.5, m: 0 }}>
              {t(`downloadAppBanner.subtitle.${deviceInfo?.operatingSystem}`)}
            </Typography>

            <Stack
              direction="row"
              alignItems="center"
              sx={{ mt: 0.5 }}
              spacing={1}
            >
              <Rating
                name="app-rating"
                value={4.8}
                readOnly
                size="small"
                sx={{ fontSize: 18 }}
                data-testid="app-rating"
              />
              <Typography
                variant="body2"
                fontWeight="fontWeightBold"
                sx={{ fontSize: 14 }}
              >
                {t('downloadAppBanner.rating')}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Stack>

      <Button
        component={Link}
        underline="none"
        href={storeLink}
        target="_blank"
        onClick={logClick}
        sx={{
          p: 1,
          minWidth: 41,
          height: 32,
          fontSize: 14,
          borderRadius: '8px',
        }}
      >
        {t('downloadAppBanner.button')}
      </Button>
    </Stack>
  );
};
